import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { api } from "@/lib/api";
import { useMetrics } from "@/context/MetricsContext";
import { ScoreChip } from "@/components/ScoreChip";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, StickyNote, Pill, Stethoscope } from "lucide-react";

export function WeeklyView({ patientId }) {
  const metrics = useMetrics();
  const [weekStart, setWeekStart] = useState(dayjs().startOf("week"));
  const [entries, setEntries] = useState([]);

  const start = weekStart.format("YYYY-MM-DD");
  const end = weekStart.add(6, "day").format("YYYY-MM-DD");

  useEffect(() => {
    const params = { start, end };
    if (patientId) params.patient_id = patientId;
    api.get("/entries", { params }).then((r) => setEntries(r.data)).catch(() => setEntries([]));
  }, [start, end, patientId]);

  const byDate = Object.fromEntries(entries.map((e) => [e.date, e]));
  const days = Array.from({ length: 7 }, (_, i) => weekStart.add(i, "day"));

  return (
    <div data-testid="weekly-view" className="animate-fade-up">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h3 className="text-xl sm:text-2xl font-serif">
          Week of {weekStart.format("MMM D")} – {weekStart.add(6, "day").format("MMM D, YYYY")}
        </h3>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" className="rounded-full" data-testid="weekly-prev"
            onClick={() => setWeekStart(weekStart.subtract(1, "week"))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-full" data-testid="weekly-next"
            onClick={() => setWeekStart(weekStart.add(1, "week"))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        {days.map((d) => {
          const key = d.format("YYYY-MM-DD");
          const e = byDate[key];
          return (
            <Card key={key} data-testid={`weekly-day-${key}`}
              className="p-4 sm:p-5 rounded-2xl border-border/70">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="sm:w-32 shrink-0">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">{d.format("dddd")}</div>
                  <div className="text-2xl font-serif">{d.format("MMM D")}</div>
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                  {metrics.map((m) => (
                    <div key={m.key} className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{m.short}</span>
                      <ScoreChip value={e?.metrics?.[m.key]} size="sm" />
                    </div>
                  ))}
                </div>
                {(e?.meds_taken || e?.doctor_visit) && (
                  <div className="flex items-center gap-3 sm:ml-auto" data-testid={`weekly-flags-${key}`}>
                    {e?.meds_taken && (
                      <span className="inline-flex items-center gap-1 text-xs text-primary" title="Took prescribed meds">
                        <Pill className="h-4 w-4" /> Meds
                      </span>
                    )}
                    {e?.doctor_visit && (
                      <span className="inline-flex items-center gap-1 text-xs text-primary" title="Had a doctor's appointment">
                        <Stethoscope className="h-4 w-4" /> Dr
                      </span>
                    )}
                  </div>
                )}
              </div>
              {e?.note && (
                <div className="mt-3 flex items-start gap-2 rounded-xl bg-secondary px-3 py-2.5"
                  data-testid={`weekly-note-${key}`}>
                  <StickyNote className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm text-foreground/90 leading-snug whitespace-pre-wrap">{e.note}</p>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
